import { api } from './client'
import type { JournalEntry, JournalEntryType, JournalLink, JournalBucketLink } from '@/types'
import { todayISO } from '@/lib/dates'

export function fetchJournalEntries(search?: string, type?: JournalEntryType | null): Promise<JournalEntry[]> {
  const params = new URLSearchParams()
  if (search?.trim()) params.set('search', search.trim())
  if (type) params.set('type', type)
  const qs = params.toString()
  return api.get(`/api/journal-entries${qs ? `?${qs}` : ''}`)
}

export function fetchJournalEntry(id: string): Promise<JournalEntry | null> {
  return api.get(`/api/journal-entries/${id}`)
}

export function fetchJournalLinks(journalId: string): Promise<JournalLink[]> {
  return api.get(`/api/journal-entries/${journalId}/links`)
}

export function fetchJournalBucketLinks(journalId: string): Promise<JournalBucketLink[]> {
  return api.get(`/api/journal-entries/${journalId}/bucket-links`)
}

export function fetchJournalLinkCounts(): Promise<Record<string, number>> {
  return api.get('/api/journal-entries/link-counts')
}

export function fetchLastWeeklyReview(): Promise<JournalEntry | null> {
  return api.get('/api/journal-entries/last-weekly-review')
}

export function fetchEntriesForPriority(priorityId: string): Promise<JournalEntry[]> {
  return api.get(`/api/journal-entries/for-priority/${priorityId}`)
}

export function fetchEntriesForBucket(bucketId: string): Promise<JournalEntry[]> {
  return api.get(`/api/journal-entries/for-bucket/${bucketId}`)
}

export async function createEntry(type: JournalEntryType = 'free', body = ''): Promise<string> {
  const { id } = await api.post<{ id: string }>('/api/journal-entries', { type, body, entryDate: todayISO() })
  return id
}

export function updateEntry(
  id: string,
  data: Partial<Pick<JournalEntry, 'body' | 'type' | 'mood' | 'aiExcludable' | 'entryDate' | 'promptUsed'>>,
): Promise<void> {
  return api.patch(`/api/journal-entries/${id}`, data)
}

export function deleteEntry(id: string): Promise<void> {
  return api.delete(`/api/journal-entries/${id}`)
}

export function addJournalLink(journalId: string, priorityId: string): Promise<void> {
  return api.post(`/api/journal-entries/${journalId}/links`, { priorityId })
}

export function removeJournalLink(journalId: string, priorityId: string): Promise<void> {
  return api.delete(`/api/journal-entries/${journalId}/links/${priorityId}`)
}

export function addJournalBucketLink(journalId: string, bucketId: string): Promise<void> {
  return api.post(`/api/journal-entries/${journalId}/bucket-links`, { bucketId })
}

export function removeJournalBucketLink(journalId: string, bucketId: string): Promise<void> {
  return api.delete(`/api/journal-entries/${journalId}/bucket-links/${bucketId}`)
}
